import { LumberProfileCatalog } from "building-types/framing/lumberTypes";
import { RoofFramingComponentType } from "building-types/framing/roofFramingComponentTypes";
import { RoofFramingType, type RoofTruss } from "building-types/framing/roofFramingTypes";
import type { RoofTrussBasicInput } from "building-types/framing/roofTrussTypes";
import { RoofTrussType } from "building-types/framing/roofTrussTypes";
import type { UnsignedNumber } from "common/types/nominalNumbersTypes";
import { unsignedNumberNew } from "common/utils/nominalNumbers";
import { lumberPiecesByProfileNew } from "doc/utils/lumberTools";
import { planeNormal } from "geom/plane";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3EvalEdge, polyLine3New } from "geom/polyLine3";
import type { TrussGenerator } from "./trussGenerator";
import {
  generateBottomChord,
  generateTopChords,
  projectChordsToFramingPlane,
  setupTopChordRails,
  setupBottomChordRails,
  setupWebChordRails,
  getBottomChordTopZ,
} from "./trussUtils";

export class FinkTrussGenerator implements TrussGenerator {
  generate(input: RoofTrussBasicInput): RoofTruss {
    const {
      leftSpan,
      leftEaveFasciaSize,
      rightSpan,
      rightEaveFasciaSize,
      leftOverhang,
      rightOverhang,
      leftTopChordPitch,
      rightTopChordPitch,
      topChordProfile,
      bottomChordProfile,
      webChordProfile,
      ridgeRafterProfile,
      bottomChordOffset,
      trussPlane,
    } = input;

    if (Array.isArray(leftSpan) || Array.isArray(rightSpan)) {
      throw new Error("Fink truss does not support multiple spans");
    }

    const ridgeRafterHalfHeight = unsignedNumberNew(LumberProfileCatalog[ridgeRafterProfile].width / 2);
    const webChordHalfHeight = unsignedNumberNew(LumberProfileCatalog[webChordProfile].width / 2);
    const bottomChordHeight = unsignedNumberNew(LumberProfileCatalog[bottomChordProfile].width);
    const topChordHeight = unsignedNumberNew(LumberProfileCatalog[topChordProfile].width);
    const topChordHalfHeight = unsignedNumberNew(topChordHeight / 2);

    const { left, right } = generateTopChords(leftSpan, rightSpan, leftTopChordPitch, rightTopChordPitch);
    const {
      bottomChord: bottomChordTopPoints,
      leftHeelChord,
      rightHeelChord,
    } = generateBottomChord(
      leftSpan,
      rightSpan,
      leftOverhang,
      rightOverhang,
      leftTopChordPitch,
      rightTopChordPitch,
      topChordHeight,
      bottomChordHeight,
      bottomChordOffset,
    );

    const diagonalWebChords = this.generateFinkDiagonalWebChords(
      leftSpan,
      leftOverhang,
      rightSpan,
      rightOverhang,
      leftTopChordPitch,
      rightTopChordPitch,
      topChordHalfHeight,
      topChordHeight,
      bottomChordHeight,
      bottomChordOffset,
      ridgeRafterHalfHeight,
      webChordHalfHeight,
    );

    const [
      leftTopChordTopPointsToFramingPlane,
      rightTopChordTopPointsToFramingPlane,
      bottomChordTopPointsToFramingPlane,
      leftHeelChordToFramingPlane,
      rightHeelChordToFramingPlane,
      ...diagonalWebChordsToFramingPlane
    ] = projectChordsToFramingPlane(
      [left, right, bottomChordTopPoints, leftHeelChord, rightHeelChord, ...diagonalWebChords],
      trussPlane,
    );

    const topChords = lumberPiecesByProfileNew(LumberProfileCatalog[topChordProfile], { [topChordProfile]: [] });
    const bottomChords = lumberPiecesByProfileNew(LumberProfileCatalog[bottomChordProfile], {
      [bottomChordProfile]: [],
    });
    const webChords = lumberPiecesByProfileNew(LumberProfileCatalog[webChordProfile], { [webChordProfile]: [] });
    const [normX, normY, normZ] = planeNormal(trussPlane);

    setupTopChordRails(topChords, leftTopChordTopPointsToFramingPlane, leftEaveFasciaSize, normX, normY, normZ);
    setupTopChordRails(topChords, rightTopChordTopPointsToFramingPlane, rightEaveFasciaSize, normX, normY, normZ);
    setupBottomChordRails(bottomChords, bottomChordTopPointsToFramingPlane, normX, normY, normZ);

    diagonalWebChordsToFramingPlane.forEach((webChord) => {
      setupWebChordRails(webChords, webChord, normX, normY, normZ);
    });

    if (leftHeelChordToFramingPlane) {
      setupWebChordRails(webChords, leftHeelChordToFramingPlane, normX, normY, normZ);
    }
    if (rightHeelChordToFramingPlane) {
      setupWebChordRails(webChords, rightHeelChordToFramingPlane, normX, normY, normZ);
    }
    return {
      trussType: RoofTrussType.FINK_TRUSS,
      components: [
        { type: RoofFramingComponentType.TOP_CHORD, lumber: topChords },
        { type: RoofFramingComponentType.BOTTOM_CHORD, lumber: bottomChords },
        { type: RoofFramingComponentType.WEB, lumber: webChords },
      ],
      framingType: RoofFramingType.TRUSS,
      plane: trussPlane,
    };
  }

  private generateFinkDiagonalWebChords(
    leftSpan: UnsignedNumber,
    leftOverhang: UnsignedNumber,
    rightSpan: UnsignedNumber,
    rightOverhang: UnsignedNumber,
    leftTopChordPitch: UnsignedNumber,
    rightTopChordPitch: UnsignedNumber,
    topChordHalfHeight: UnsignedNumber,
    topChordHeight: UnsignedNumber,
    bottomChordHeight: UnsignedNumber,
    bottomChordOffset: number,
    ridgeRafterHalfHeight: UnsignedNumber,
    _webChordHalfHeight: UnsignedNumber,
  ): [PolyLine3, PolyLine3, PolyLine3, PolyLine3] {
    const costhetaLeft = 1 / Math.sqrt(1 + leftTopChordPitch * leftTopChordPitch);
    const sinthetaLeft = leftTopChordPitch * costhetaLeft;

    const leftWebChordTopX = -(leftSpan - leftOverhang) * 0.5 + topChordHalfHeight * sinthetaLeft;
    const leftWebChordTopZ = -(leftSpan - leftOverhang) * 0.5 * leftTopChordPitch - topChordHalfHeight * costhetaLeft;

    const costhetaRight = 1 / Math.sqrt(1 + rightTopChordPitch * rightTopChordPitch);
    const sinthetaRight = rightTopChordPitch * costhetaRight;

    const rightWebChordTopX = (rightSpan - rightOverhang) * 0.5 - topChordHalfHeight * sinthetaRight;
    const rightWebChordTopZ =
      -(rightSpan - rightOverhang) * 0.5 * rightTopChordPitch - topChordHalfHeight * costhetaRight;

    const { bottomChordTopZ } = getBottomChordTopZ(
      leftSpan,
      rightSpan,
      leftOverhang,
      rightOverhang,
      leftTopChordPitch,
      rightTopChordPitch,
      topChordHeight,
      bottomChordHeight,
      bottomChordOffset,
    );

    const bottomChord = polyLine3New([
      -Math.abs(bottomChordTopZ) / leftTopChordPitch,
      0,
      bottomChordTopZ,
      Math.abs(bottomChordTopZ) / rightTopChordPitch,
      0,
      bottomChordTopZ,
    ]);

    const [xLeftBottom, yLeftBottom, zLeftBottom] = polyLine3EvalEdge(bottomChord, 0, 1 / 3);
    const [xLeftTop, yLeftTop, zLeftTop] = [leftWebChordTopX, 0, leftWebChordTopZ];
    const [xRightBottom, yRightBottom, zRightBottom] = polyLine3EvalEdge(bottomChord, 0, 2 / 3);
    const [xRightTop, yRightTop, zRightTop] = [rightWebChordTopX, 0, rightWebChordTopZ];
    const [xMiddleTop, yMiddleTop, zMiddleTop] = [0, 0, -ridgeRafterHalfHeight];

    const webChord1 = polyLine3New([xLeftBottom, yLeftBottom, zLeftBottom, xLeftTop, yLeftTop, zLeftTop]);
    const webChord2 = polyLine3New([xLeftBottom, yLeftBottom, zLeftBottom, xMiddleTop, yMiddleTop, zMiddleTop]);
    const webChord3 = polyLine3New([xMiddleTop, yMiddleTop, zMiddleTop, xRightBottom, yRightBottom, zRightBottom]);
    const webChord4 = polyLine3New([xRightTop, yRightTop, zRightTop, xRightBottom, yRightBottom, zRightBottom]);

    return [webChord1, webChord2, webChord3, webChord4];
  }
}
